import React, { useCallback, useEffect, useRef, useState } from 'react'
import Icon from '@components/atoms/Icon'

interface PromotionalMessage {
  tagline: string
  heading: string
  subheading?: string
  primary_color?: string
  gradient_color?: string
}

export interface PromotionalData {
  enabled_banner: boolean
  promotional_title?: string
  promotions: PromotionalMessage[]
}

const BANNER_HIDDEN_ATTRIBUTE = 'bannerHidden'
const SCROLL_HIDDEN_ATTRIBUTE = 'scrollHidden'

const PromotionalBar: React.FC<PromotionalData> = ({ promotions, promotional_title, enabled_banner }) => {
  const [isBannerExpanded, setIsBannerExpanded] = useState(true)
  const [isStickyVisible, setIsStickyVisible] = useState(true)
  const [bannerHeight, setBannerHeight] = useState<number | null>(null)
  const bannerRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLElement | null>(null)
  const lastScrollPositionRef = useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0)

  if (!enabled_banner || !promotions?.length) {
    return null
  }

  const resolveContainer = useCallback(() => {
    if (typeof document === 'undefined') {
      return null
    }

    if (bannerRef.current) {
      return bannerRef.current.closest('[data-promotional-banner-container]') as HTMLElement | null
    }

    return document.querySelector('[data-promotional-banner-container]') as HTMLElement | null
  }, [])

  const handleBannerToggle = useCallback(() => {
    setIsBannerExpanded((prev) => !prev)
  }, [])

  useEffect(() => {
    containerRef.current = resolveContainer()
  }, [resolveContainer, isBannerExpanded])

  useEffect(() => {
    const container = containerRef.current || resolveContainer()

    if (!container) {
      return
    }

    container.dataset[BANNER_HIDDEN_ATTRIBUTE] = isBannerExpanded ? 'false' : 'true'
  }, [isBannerExpanded, resolveContainer])

  useEffect(() => {
    const container = containerRef.current || resolveContainer()

    if (!container) {
      return
    }

    container.dataset[SCROLL_HIDDEN_ATTRIBUTE] = isStickyVisible ? 'false' : 'true'
  }, [isStickyVisible, resolveContainer])

  const updateBannerHeight = useCallback(() => {
    if (!bannerRef.current) {
      return
    }

    setBannerHeight(bannerRef.current.scrollHeight)
  }, [])

  useEffect(() => {
    updateBannerHeight()
  }, [promotions, promotional_title, isBannerExpanded, updateBannerHeight])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleResize = () => {
      if (!isBannerExpanded) {
        return
      }

      updateBannerHeight()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isBannerExpanded, updateBannerHeight])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleScroll = () => {
      const currentScrollPosition = window.scrollY
      const isScrollingDown = currentScrollPosition > lastScrollPositionRef.current
      const isAtTop = currentScrollPosition === 0

      setIsStickyVisible((prev) => {
        if (isAtTop) {
          return true
        }

        if (isScrollingDown && prev) {
          return false
        }

        if (!isScrollingDown && !prev) {
          return true
        }

        return prev
      })

      lastScrollPositionRef.current = currentScrollPosition
    }

    handleScroll()

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (!isBannerExpanded) {
      return
    }

    if (typeof window !== 'undefined') {
      lastScrollPositionRef.current = window.scrollY
    }

    setIsStickyVisible(true)
  }, [isBannerExpanded])

  const bannerAnimationStyles: React.CSSProperties = {
    maxHeight: isBannerExpanded
      ? bannerHeight !== null
        ? `${bannerHeight}px`
        : undefined
      : '0px',
    opacity: isBannerExpanded ? 1 : 0,
    overflow: 'hidden',
    transition: 'max-height 300ms ease, opacity 300ms ease',
    backdropFilter: 'blur(0.3438rem)',
    background: "rgba(0, 0, 0, 0.90)"
  }

  return (
    <div>
      <div
        id="promotional-banner-content"
        ref={bannerRef}
        className="relative w-full px-3 pt-[25px] pb-4 lg:px-5 sticky z-10 border-2 border-solid border-[#333] w-full mx-auto rounded-[10px] lg:w-[calc(100%-20px)] transition-transform ease-in-out duration-300 will-change-transform"
        style={bannerAnimationStyles}
        aria-hidden={!isBannerExpanded}
        data-promotional-banner-content
      >
        <div className="w-full">
          <div className="flex flex-1 flex-nowrap items-center justify-center gap-4 text-sm mb-[10px] mx-auto w-full lg:w-[80%] lg:gap-[100px] xl:gap-[200px] lg:mb-[20px]">
            {promotions.map(
              ({ tagline, heading, subheading, primary_color, gradient_color }, index) => {
              const key = `${tagline || heading || subheading || 'promotion'}-${index}`
              const gradientBackground = gradient_color
              const colorStyle: React.CSSProperties = primary_color ? { color: primary_color } : {}
              const headingStyle: React.CSSProperties = {
                background: gradientBackground,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: '"Orbitron", sans-serif',
                ...colorStyle,
              };

              (headingStyle as Record<string, string>).backgroundClip = 'text'

              return (
                <div key={key} className="flex flex-col items-center text-center uppercase lg:items-center">
                  {tagline && (
                    <span className="text-[8px] text-white tracking-wide opacity-80 lg:text-[10px]" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
                      {tagline}
                    </span>
                  )}
                  <h5 className="text-[24px] leading-[24px] font-bold tracking-[-0.96px] lg:tracking-[-1.68px] lg:text-[42px] lg:leading-[42px]" style={headingStyle}>
                    {heading}
                  </h5>
                  {subheading && (
                    <span className="text-[12px] leading-[24px] font-bold uppercase lg:text-[16px] lg:leading-[24px]" style={colorStyle}>
                      {subheading}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
          {promotional_title && (
            <div className="text-[9px] leading-[13px] font-bold uppercase tracking-[0.32px] text-center text-white opacity-30 underline lg:text-[11px] lg:tracking-[0.36px]">
              {promotional_title}
            </div>
          )}
        </div>

        <button
          data-close-announcement
          type="button"
          className="flex items-center justify-center w-8 h-8 absolute z-5 top-[5px] right-0 lg:right-2.5 lg:top-[12px]"
          onClick={handleBannerToggle}
          aria-label="Close promotional banner"
        >
          <Icon name="close" className="text-white opacity-30 h-5 w-5" />
        </button>
      </div>

      <button 
        type="button" 
        role="button" 
        aria-label="Toggle promotional banner visibility" 
        aria-controls="promotional-banner-content"
        aria-expanded={isBannerExpanded}
        onClick={handleBannerToggle}
        className="w-[60px] h-[30px] absolute inset-x-0 -bottom-7 mx-auto z-10 pt-2 px-2.5 pb-2.5 border-2 border-solid border-[#333] border-t-0 rounded-bl-[10px] rounded-br-[10px] transition-colors duration-200"
        style={{ background: "rgba(0, 0, 0, 0.90)" }} 
        data-banner-caret 
        >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="7 8 10 7"
          fill="none"
          className="text-white w-full h-full"
          style={{ transform: isBannerExpanded ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 200ms ease' }}
        >
          <path fillRule="evenodd" clipRule="evenodd" d="M16.5303 8.96967C16.8232 9.26256 16.8232 9.73744 16.5303 10.0303L12.5303 14.0303C12.2374 14.3232 11.7626 14.3232 11.4697 14.0303L7.46967 10.0303C7.17678 9.73744 7.17678 9.26256 7.46967 8.96967C7.76256 8.67678 8.23744 8.67678 8.53033 8.96967L12 12.4393L15.4697 8.96967C15.7626 8.67678 16.2374 8.67678 16.5303 8.96967Z" fill="currentColor"/>
        </svg>
      </button>
    </div>
  )
}

export default PromotionalBar
